import React from 'react';
import { ModalReact, ModalType } from 'common/components/modal';
import { ButtonTheme } from 'common/components/button/constants';
import { useTranslation } from 'common/i18n/hooks';
import { ButtonReact } from 'common/components/button';
import { InputReact } from 'common/components/input';
import { InputSize } from 'common/components/input/types';
import { InputLabelReact } from 'common/components/input/label';
import { useDecideExperiment } from 'iron/apps/experiments/src/components/hooks/decide-experiment';
import { ExperimentCollectionType } from 'apps/experiments/src/types';
import { RadioButtonReact } from 'common/components/radio-button';
import { useVariants } from 'iron/apps/experiments/src/components/hooks/metric-results';
import { DEFAULT_CONTROL_KEY } from 'iron/apps/experiments/src/components/utils';
import { useStagedExperiment } from 'iron/apps/experiments/src/components/hooks/staged-experiment';
import styles from './_decide-modal.module.styl';
type ExperimentPageBannerDecideModalProps = {
  onClose: () => void;
};
export function ExperimentPageBannerDecideModal(props: ExperimentPageBannerDecideModalProps) {
  var _experiment$settings$, _experiment$settings, _experiment$results_c, _experiment$results_c2, _experiment$settings2;
  const {
    t
  } = useTranslation();
  const {
    stagedExperiment: experiment
  } = useStagedExperiment();
  const decideExperiment = useDecideExperiment();
  const variants = useVariants();
  const controlKey = (_experiment$settings$ = experiment === null || experiment === void 0 || (_experiment$settings = experiment.settings) === null || _experiment$settings === void 0 ? void 0 : _experiment$settings.controlKey) !== null && _experiment$settings$ !== void 0 ? _experiment$settings$ : DEFAULT_CONTROL_KEY;
  variants.delete(controlKey);
  const [message, setMessage] = React.useState((_experiment$results_c = experiment === null || experiment === void 0 || (_experiment$results_c2 = experiment.results_cache) === null || _experiment$results_c2 === void 0 ? void 0 : _experiment$results_c2.message) !== null && _experiment$results_c !== void 0 ? _experiment$results_c : "");
  const [selectedVariant, setSelectedVariant] = React.useState<string | undefined>(() => {
    var _experiment$results_c3, _experiment$results_c4;
    if (variants.has((_experiment$results_c3 = experiment === null || experiment === void 0 || (_experiment$results_c4 = experiment.results_cache) === null || _experiment$results_c4 === void 0 ? void 0 : _experiment$results_c4.variant) !== null && _experiment$results_c3 !== void 0 ? _experiment$results_c3 : "")) {
      var _experiment$results_c5;
      return experiment === null || experiment === void 0 || (_experiment$results_c5 = experiment.results_cache) === null || _experiment$results_c5 === void 0 ? void 0 : _experiment$results_c5.variant;
    }
    return controlKey;
  });
  const handleDecision = React.useCallback(() => {
    decideExperiment({
      success: Boolean(selectedVariant) && selectedVariant !== controlKey,
      variant: selectedVariant,
      message
    });
  }, [message, selectedVariant, decideExperiment]);
  const isFF = (experiment === null || experiment === void 0 || (_experiment$settings2 = experiment.settings) === null || _experiment$settings2 === void 0 ? void 0 : _experiment$settings2.collectionMethod) === ExperimentCollectionType.FEATURE_FLAG;
  const description = isFF ? t("experiments.banner.decide.description-feature-flag") : t("experiments.banner.decide.description-exposure");
  const variantOptions = [{
    value: controlKey,
    label: t("experiments.banner.decide.control-label", {
      controlKey
    })
  }, ...Array.from(variants).map((variant, i) => ({
    value: variant,
    label: t("experiments.banner.decide.variant-label", {
      variant,
      num: i + 1
    })
  })), {
    value: undefined,
    label: t("experiments.banner.decide.none")
  }];
  const selectedVariantOption = variantOptions.find(option => option.value === selectedVariant);
  return <ModalReact className={styles.modal} modalType={ModalType.Modal} modalTitle={t("experiments.banner.decide.title")} modalDescription={description} open data-sentry-element="ModalReact" data-sentry-component="ExperimentPageBannerDecideModal" data-sentry-source-file="_decide-modal.tsx">
      <div className={styles.modalBody}>
        <div className={styles.title}>{t("experiments.banner.decide.title")}</div>
        <div>{description}</div>
        <div className={styles.form}>
          <div>
            <InputLabelReact fieldId='decide-variants' label={t("experiments.banner.decide.variants")} data-sentry-element="InputLabelReact" data-sentry-source-file="_decide-modal.tsx" />
            <div className={styles.variantsWrapper}>
              {variantOptions.map(_ref => {
              let {
                value,
                label
              } = _ref;
              return <RadioButtonReact key={value !== null && value !== void 0 ? value : "__none"} className={styles.radioBtn} selected={selectedVariant === value} onClick={() => setSelectedVariant(value)}>
                  {label}
                </RadioButtonReact>;
            })}
            </div>
          </div>
          <div>
            <InputLabelReact fieldId='decide-message' label={t("experiments.banner.decide.message-label")} extraInfo={t("experiments.banner.decide.message-helper")} data-sentry-element="InputLabelReact" data-sentry-source-file="_decide-modal.tsx" />
            <InputReact className={styles.fullWidth} size={InputSize.Large} type='textarea' placeholder={t("experiments.banner.decide.message-placeholder")} value={message} onInput={ev => {
            setMessage(ev.target.value);
          }} data-sentry-element="InputReact" data-sentry-source-file="_decide-modal.tsx" />
          </div>
          <div className={styles.formActions}>
            <ButtonReact theme={ButtonTheme.Tertiary} onClick={() => {
            props.onClose();
          }} data-sentry-element="ButtonReact" data-sentry-source-file="_decide-modal.tsx">
              {t("common.cancel")}
            </ButtonReact>
            <ButtonReact theme={ButtonTheme.Primary} onClick={() => {
            handleDecision();
            props.onClose();
          }} data-sentry-element="ButtonReact" data-sentry-source-file="_decide-modal.tsx">
              {isFF ? t("experiments.banner.decide.ship", {
              label: selectedVariantOption === null || selectedVariantOption === void 0 ? void 0 : selectedVariantOption.label
            }) : t("experiments.banner.decide.make-decision")}
            </ButtonReact>
          </div>
        </div>
      </div>
    </ModalReact>;
}